// apps/lk/src/lib/auth.tsx
'use client';
import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { api } from './api';
import { setToken, getToken, setRefreshToken, getRefreshToken, refreshAccessToken, clearAuth } from '@tb/api-client/client';

type User = { id: string; email: string; firstName?: string; lastName?: string; displayName?: string; avatarUrl?: string; role?: string; gender?: string | null; birthDate?: string | null };
type AuthCtx = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
 const [user, setUser] = useState<User | null>(null);
 const [loading, setLoading] = useState(true);

 const doInit = useCallback(async () => {
 const t = getToken();
 if (!t) { setLoading(false); return; }
 setToken(t);
 try {
 const rt = getRefreshToken();
 if (rt) await refreshAccessToken();
 const r: any = await api.me();
 setUser(r?.data?.user ?? null);
 } catch {
 // Network error — keep stale token, don't clearAuth
 } finally {
 setLoading(false);
 }
 }, []);

 useEffect(() => {
 doInit();

 // Proactive refresh every 10 min
 const interval = setInterval(async () => {
 const t = getToken();
 const rt = getRefreshToken();
 if (!t || !rt) return;
 try { await refreshAccessToken(); } catch {}
 }, 10 * 60 * 1000);

 // Re-validate on tab focus — но не чаще чем раз в 5 минут
 const onFocus = () => {
 const now = Date.now();
 const last = (window as any).__lastFocusCheck || 0;
 if (now - last < 5 * 60 * 1000) return; // тротл 5 мин
 (window as any).__lastFocusCheck = now;
 const t = getToken();
 const rt = getRefreshToken();
 if (t && rt) {
 refreshAccessToken().then(() => api.me().then((r: any) => setUser(r?.data?.user ?? null)).catch(() => {}));
 }
 };
 window.addEventListener('focus', onFocus);

 return () => {
 clearInterval(interval);
 window.removeEventListener('focus', onFocus);
 };
 }, [doInit]);

 const login = async (email: string, password: string) => {
 const r: any = await api.login(email, password);
 const { accessToken, refreshToken, user: u } = r?.data ?? {};
 if (accessToken) {
 setToken(accessToken);
 if (refreshToken) setRefreshToken(refreshToken);
 }
 setUser(u);
 };

 const register = async (email: string, password: string, firstName?: string, lastName?: string) => {
   const r: any = await api.register(email, password, firstName, lastName);
 const { accessToken, refreshToken, user: u } = r?.data ?? {};
 if (accessToken) {
 setToken(accessToken);
 if (refreshToken) setRefreshToken(refreshToken);
 }
 setUser(u);
 };

 const logout = () => {
 api.logout().catch(() => {});
 clearAuth();
 setUser(null);
 window.location.href = '/login';
 };

 const refreshUser = async () => {
 try {
 const r: any = await api.me();
 const u = r?.data?.user ?? null;
 if (u) setUser(u);
 } catch { /* ignore */ }
 };

 return <Ctx.Provider value={{ user, loading, login, register, logout, refreshUser }}>{children}</Ctx.Provider>;
}

export function useAuth() {
 const c = useContext(Ctx);
 if (!c) throw new Error('useAuth outside AuthProvider');
 return c;
}
